(() => {
  const STORAGE_KEY = 'gooeyContinuity:v1';
  const VERSION = 1;
  const MAX_PRODUCTS = 8;
  const MAX_SEARCHES = 5;
  const MAX_COLLECTIONS = 5;
  const MAX_COMMENTS = 24;

  const clone = value => {
    try { return JSON.parse(JSON.stringify(value)); }
    catch (error) { return value; }
  };

  const blankMemory = () => ({
    version: VERSION,
    recentProducts: [],
    familyCounts: {},
    recentSearches: [],
    recentCollections: [],
    cart: {
      initialized: false,
      count: 0,
      items: [],
      lastAction: null,
      lastProduct: null,
      at: null
    },
    rewards: {
      status: 'unknown',
      joinedThisSession: false,
      joinedAt: null,
      at: null
    },
    comments: [],
    lastPage: null,
    updatedAt: null
  });

  const load = () => {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!parsed || parsed.version !== VERSION) return blankMemory();
      return {
        ...blankMemory(),
        ...parsed,
        cart: { ...blankMemory().cart, ...(parsed.cart || {}) },
        rewards: { ...blankMemory().rewards, ...(parsed.rewards || {}) }
      };
    } catch (error) {
      return blankMemory();
    }
  };

  let memory = load();
  let refreshQueued = false;

  const save = (action, emit = true) => {
    memory.updatedAt = Date.now();
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); }
    catch (error) { /* Continuity still works in memory if storage is unavailable. */ }
    if (emit) {
      document.dispatchEvent(new CustomEvent('gooey:continuity:change', {
        detail: { action, memory: clone(memory) }
      }));
    }
  };

  const update = (action, mutator, emit = true) => {
    const next = clone(memory);
    const changed = mutator(next) === true;
    if (!changed) return false;
    memory = next;
    save(action, emit);
    return true;
  };

  const safeLower = value => String(value || '').trim().toLowerCase();
  const handleFromUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const familyLabels = {
    smoky: 'smoky',
    woodsy: 'woodsy',
    coastal: 'coastal',
    citrus: 'bright citrus',
    herbal: 'green and herbal',
    sweet: 'sweet',
    floral: 'floral',
    spiced: 'spiced',
    fresh: 'fresh'
  };

  const familyKeywords = {
    smoky: ['smoke', 'smoky', 'campfire', 'bonfire', 'whiskey', 'bourbon', 'tobacco', 'leather', 'dark rum', 'embers'],
    woodsy: ['cedar', 'oak', 'birch', 'driftwood', 'sandalwood', 'moss', 'oakmoss', 'cypress', 'wood'],
    coastal: ['sea salt', 'ocean', 'coastal', 'marine', 'rain', 'water', 'ozone', 'shore', 'tide'],
    citrus: ['grapefruit', 'orange', 'lemon', 'bergamot', 'citrus', 'mandarin', 'citron'],
    herbal: ['sage', 'eucalyptus', 'mint', 'grass', 'herbal', 'green', 'verbena'],
    sweet: ['vanilla', 'caramel', 'cake', 'honey', 'tonka', 'pear', 'plum', 'coconut'],
    floral: ['rose', 'lavender', 'orchid', 'tulip', 'floral', 'flower'],
    spiced: ['pepper', 'cinnamon', 'spice', 'chai', 'clove'],
    fresh: ['clean', 'fresh', 'air', 'linen']
  };

  const classifyScent = value => {
    const text = safeLower(value);
    if (!text) return '';

    if (text.includes('big wick energy')) return 'smoky';
    if (text.includes('morning wood')) return 'woodsy';
    if (text.includes('low tide')) return 'coastal';
    if (text.includes('shuck around')) return 'spiced';
    if (text.includes('salty little secret')) return 'citrus';
    if (text.includes("blowin' smoke") || text.includes('blowin smoke')) return 'smoky';

    const scores = Object.entries(familyKeywords).map(([family, keywords]) => ({
      family,
      score: keywords.reduce((sum, keyword) => sum + (text.includes(keyword) ? 1 : 0), 0)
    }));
    scores.sort((left, right) => right.score - left.score);
    return scores[0]?.score > 0 ? scores[0].family : '';
  };

  const rebuildFamilyCounts = target => {
    const counts = {};
    target.recentProducts.forEach(product => {
      if (!product.family) return;
      counts[product.family] = (counts[product.family] || 0) + 1;
    });
    target.familyCounts = counts;
  };

  const dominantFamily = source => {
    const target = source || memory;
    const entries = Object.entries(target.familyCounts || {})
      .map(([family, count]) => ({ family, count: Number(count || 0) }))
      .sort((left, right) => right.count - left.count);
    return entries[0]?.count >= 2 ? entries[0] : null;
  };

  const familyLabel = family => familyLabels[family] || family || 'that';

  const currentPageType = () => {
    if (document.querySelector('[data-product-section]')) return 'product';
    if (document.querySelector('.collection-page')) return 'collection';
    if (document.querySelector('.search-page')) return 'search';
    if (document.querySelector('.cart-page')) return 'cart';
    if (document.querySelector('[data-gdr-widget="page"]')) return 'rewards';
    if (document.querySelector('[data-gooey-404-root]')) return '404';
    return 'other';
  };

  const currentPageKey = () => `${window.location.pathname}${window.location.search}`;

  const recordProductView = () => {
    const section = document.querySelector('[data-product-section]');
    if (!section) return;
    const handle = handleFromUrl(window.location.href);
    const title = section.querySelector('.product-info .h2')?.textContent?.trim() || document.querySelector('h1')?.textContent?.trim() || handle;
    const notes = section.querySelector('.product-info__notes')?.textContent?.trim() || '';
    const family = classifyScent(`${title} ${notes}`);
    const pageKey = currentPageKey();
    const alreadyCurrent = memory.lastPage?.type === 'product' && memory.lastPage?.key === pageKey;
    if (!handle || alreadyCurrent) return;

    update('product-view', next => {
      next.recentProducts.unshift({ handle, title, notes, family, at: Date.now() });
      next.recentProducts = next.recentProducts.slice(0, MAX_PRODUCTS);
      rebuildFamilyCounts(next);
      return true;
    });
  };

  const recordSearch = () => {
    const page = document.querySelector('.search-page');
    const meta = page?.querySelector('.search-meta');
    if (!page || !meta) return;
    const term = page.querySelector('input[name="q"]')?.value?.trim() || '';
    const count = Number((meta.querySelector('strong')?.textContent || '0').replace(/[^0-9-]/g, '')) || 0;
    const key = `${safeLower(term)}:${count}:${currentPageKey()}`;
    if (memory.recentSearches[0]?.key === key) return;

    update('search', next => {
      next.recentSearches.unshift({ key, term, count, at: Date.now() });
      next.recentSearches = next.recentSearches.slice(0, MAX_SEARCHES);
      return true;
    });
  };

  const recordCollection = () => {
    const page = document.querySelector('.collection-page');
    if (!page) return;
    const url = new URL(window.location.href);
    const filters = [...url.searchParams.entries()]
      .filter(([key]) => key.startsWith('filter.'))
      .map(([key, value]) => `${key}=${value}`);
    const sort = url.searchParams.get('sort_by') || '';
    if (!filters.length && !sort) return;
    const key = `${url.pathname}?${[...filters, sort ? `sort_by=${sort}` : ''].filter(Boolean).join('&')}`;
    if (memory.recentCollections[0]?.key === key) return;

    update('collection-choice', next => {
      next.recentCollections.unshift({ key, path: url.pathname, filters, sort, at: Date.now() });
      next.recentCollections = next.recentCollections.slice(0, MAX_COLLECTIONS);
      return true;
    });
  };

  const rewardStatus = root => {
    if (!root) return 'unknown';
    const status = root.querySelector('[data-gdr-status]');
    const statusText = status?.textContent?.trim() || '';
    if (status?.dataset.tone === 'success' || /welcome to gooey duck rewards/i.test(statusText)) return 'joined';
    const title = root.querySelector('[data-gdr-page-title]')?.textContent?.trim() || '';
    const primary = root.querySelector('[data-gdr-page-primary]')?.textContent || '';
    if (/^you['’]ve got\s+\d+/i.test(title) || /view my rewards/i.test(primary)) return 'member';
    if (root.querySelector('[data-gdr-action="join"]')) return 'ready';
    return 'signed-out';
  };

  const syncRewards = () => {
    const root = document.querySelector('[data-gdr-widget="page"]');
    if (!root) return;
    const status = rewardStatus(root);
    if (status === memory.rewards.status) return;

    update('rewards-state', next => {
      const previous = next.rewards.status;
      next.rewards.status = status;
      next.rewards.at = Date.now();
      if (status === 'joined' || (status === 'member' && previous !== 'unknown' && previous !== 'member')) {
        next.rewards.joinedThisSession = true;
        next.rewards.joinedAt = Date.now();
      }
      return true;
    });
  };

  const itemFamily = (handle, title) => {
    const viewed = memory.recentProducts.find(product => product.handle === handle);
    return viewed?.family || classifyScent(title);
  };

  const drawerItems = () => {
    const host = document.querySelector('#CartDrawerContent');
    if (!host) return [];
    return [...host.querySelectorAll('.drawer-line')].map(line => {
      const link = line.querySelector('.drawer-line__title');
      const quantity = Number(line.querySelector('.drawer-line__qty span')?.textContent || 0) || 0;
      const handle = handleFromUrl(link?.href || '');
      const title = link?.textContent?.trim() || handle;
      return { handle, title, quantity, family: itemFamily(handle, title) };
    }).filter(item => item.handle || item.title);
  };

  const cartPageItems = () => [...document.querySelectorAll('.cart-page .cart-line')].map(line => {
    const link = line.querySelector('.cart-line__info > a');
    const input = line.querySelector('input[name="updates[]"]');
    const handle = handleFromUrl(link?.href || '');
    const title = link?.textContent?.trim() || handle;
    return { handle, title, quantity: Number(input?.value || 0) || 0, family: itemFamily(handle, title) };
  }).filter(item => item.handle || item.title);

  const cartCountFromDom = () => Number((document.querySelector('[data-cart-count]')?.textContent || '0').replace(/[^0-9]/g, '')) || 0;

  const sameItems = (left, right) => JSON.stringify(left || []) === JSON.stringify(right || []);

  const changedProduct = (previousItems, currentItems, direction) => {
    const previous = new Map((previousItems || []).map(item => [item.handle || item.title, item]));
    const current = new Map((currentItems || []).map(item => [item.handle || item.title, item]));
    const pool = direction === 'added' ? currentItems : previousItems;
    return (pool || []).find(item => {
      const key = item.handle || item.title;
      const before = Number(previous.get(key)?.quantity || 0);
      const after = Number(current.get(key)?.quantity || 0);
      return direction === 'added' ? after > before : after < before;
    }) || null;
  };

  const syncCart = () => {
    const pageItems = cartPageItems();
    const drawer = drawerItems();
    const items = pageItems.length ? pageItems : drawer;
    const domCount = cartCountFromDom();
    const itemCount = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const count = items.length ? itemCount : domCount;
    const previous = memory.cart;

    if (!previous.initialized) {
      update('cart-initialized', next => {
        next.cart.initialized = true;
        next.cart.count = count;
        next.cart.items = items;
        next.cart.lastAction = null;
        next.cart.lastProduct = null;
        next.cart.at = Date.now();
        return true;
      });
      return;
    }

    if (count === previous.count && sameItems(items, previous.items)) return;

    update('cart-change', next => {
      const prior = next.cart;
      let action = null;
      let product = null;
      if (count > prior.count) {
        action = 'added';
        product = changedProduct(prior.items, items, 'added');
      } else if (count < prior.count) {
        action = 'removed';
        product = changedProduct(prior.items, items, 'removed');
      }
      next.cart.count = count;
      next.cart.items = items;
      if (action) {
        next.cart.lastAction = action;
        next.cart.lastProduct = product;
        next.cart.at = Date.now();
      }
      return true;
    });
  };

  const touchPage = () => {
    const type = currentPageType();
    const key = currentPageKey();
    if (memory.lastPage?.type === type && memory.lastPage?.key === key) return;
    update('page', next => {
      next.lastPage = { type, key, at: Date.now() };
      return true;
    }, false);
  };

  const hasRescueContext = snapshot => Boolean(
    snapshot?.currentRoom ||
    snapshot?.currentSituation ||
    snapshot?.currentProblem ||
    snapshot?.continuation?.room ||
    snapshot?.continuation?.situation ||
    snapshot?.scentMatch?.primary
  );

  const recentEnough = (at, minutes = 15) => Boolean(at && (Date.now() - Number(at)) < minutes * 60 * 1000);

  const decorateProductTake = () => {
    const take = document.querySelector('.product-gooey-take');
    const section = document.querySelector('[data-product-section]');
    if (!take || !section) return;
    const snapshot = window.GooeyExperience?.state?.get?.() || null;
    if (hasRescueContext(snapshot)) return;

    const label = take.querySelector('.product-gooey-take__label');
    const copy = take.querySelector('.product-gooey-take__copy p');
    const figure = take.querySelector('.product-gooey-take__figure img');
    if (!label || !copy) return;

    if (!take.dataset.gooeyBaseLabel) take.dataset.gooeyBaseLabel = label.textContent || "GOOEY'S TAKE";
    if (!take.dataset.gooeyBaseCopy) take.dataset.gooeyBaseCopy = copy.textContent || '';
    if (figure && !take.dataset.gooeyBaseImage) take.dataset.gooeyBaseImage = figure.src || '';

    const handle = handleFromUrl(window.location.href);
    const title = section.querySelector('.product-info .h2')?.textContent?.trim() || handle;
    const notes = section.querySelector('.product-info__notes')?.textContent?.trim() || '';
    const family = classifyScent(`${title} ${notes}`);
    const sameViews = memory.recentProducts.filter(product => product.handle === handle).length;
    const familyViews = memory.recentProducts.filter(product => product.family && product.family === family).length;
    const base = take.dataset.gooeyBaseCopy;

    let nextLabel = take.dataset.gooeyBaseLabel;
    let nextCopy = base;
    let pose = '';

    if (sameViews >= 2) {
      nextLabel = 'GOOEY REMEMBERS';
      nextCopy = `Back to ${title}. I noticed. ${base}`;
      pose = 'sideEye';
    } else if (family && familyViews >= 3) {
      nextLabel = 'GOOEY NOTICED';
      nextCopy = `Still circling the ${familyLabel(family)} stuff. ${base}`;
      pose = 'impressed';
    } else if (memory.cart.lastAction === 'removed' && memory.cart.lastProduct?.handle === handle && recentEnough(memory.cart.at, 10)) {
      nextLabel = 'GOOEY REMEMBERS';
      nextCopy = `Back already? So removing it was dramatic. ${base}`;
      pose = 'sideEye';
    }

    if (label.textContent !== nextLabel) label.textContent = nextLabel;
    if (copy.textContent !== nextCopy) copy.textContent = nextCopy;
    if (figure) {
      const nextImage = pose ? (window.GooeyDuck?.mascotAssets?.[pose] || take.dataset.gooeyBaseImage) : take.dataset.gooeyBaseImage;
      if (nextImage && figure.src !== nextImage) figure.src = nextImage;
    }
  };

  const decorateConcierge = () => {
    const dock = document.querySelector('[data-gooey-concierge-dock]');
    if (!dock) return;
    const copy = dock.querySelector('.gooey-layer3-dock-hero__speech .gooey-layer3-speech__copy');
    const figure = dock.querySelector('.gooey-layer3-dock-hero__figure img');
    if (!copy) return;

    if (!dock.dataset.gooeyBaseCopy) dock.dataset.gooeyBaseCopy = copy.textContent || 'Need a hand? I have opinions.';
    if (figure && !dock.dataset.gooeyBaseImage) dock.dataset.gooeyBaseImage = figure.src || '';

    const latestSearch = memory.recentSearches[0];
    const dominant = dominantFamily(memory);
    let line = dock.dataset.gooeyBaseCopy;
    let pose = 'sideEye';

    if (memory.cart.lastAction === 'removed' && recentEnough(memory.cart.at, 10)) {
      line = 'Changed your mind? Fine. Pick another one.';
      pose = 'sideEye';
    } else if (memory.rewards.joinedThisSession && recentEnough(memory.rewards.joinedAt, 20)) {
      line = "Rewards are on. Now let's find something worth the points.";
      pose = 'approved';
    } else if (latestSearch?.count === 0 && recentEnough(latestSearch.at, 10)) {
      line = 'That search went nowhere. Want help?';
      pose = 'concerned';
    } else if (dominant) {
      line = `Still circling the ${familyLabel(dominant.family)} stuff? I noticed.`;
      pose = 'impressed';
    }

    if (copy.textContent !== line) copy.textContent = line;
    if (figure) {
      const nextImage = window.GooeyDuck?.mascotAssets?.[pose] || dock.dataset.gooeyBaseImage;
      if (nextImage && figure.src !== nextImage) figure.src = nextImage;
    }
  };

  const markComment = key => {
    if (!key || memory.comments.includes(key)) return;
    update('comment', next => {
      next.comments.unshift(key);
      next.comments = [...new Set(next.comments)].slice(0, MAX_COMMENTS);
      return true;
    }, false);
  };

  const sync = () => {
    recordProductView();
    recordSearch();
    recordCollection();
    syncRewards();
    syncCart();
    touchPage();
    decorateProductTake();
    decorateConcierge();
  };

  const refresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(() => {
      refreshQueued = false;
      sync();
    });
  };

  window.GooeyContinuity = {
    get: () => clone(memory),
    classifyScent,
    dominantFamily: source => dominantFamily(source || memory),
    familyLabel,
    hasComment: key => memory.comments.includes(key),
    markComment,
    reset() {
      memory = blankMemory();
      save('reset');
      refresh();
    }
  };

  const observer = new MutationObserver(refresh);
  const start = () => {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'data-tone', 'hidden']
    });
    refresh();
  };

  document.addEventListener('gooey:experience:statechange', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('change', refresh);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();